import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useForm as useHookForm, FormProvider } from 'react-hook-form';
import { useTable } from './hooks';
import { Footer, TableTabbed } from 'components';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { useYupValidationResolver } from '../../helpers/form';
import { DevTool } from '@hookform/devtools';
import { useSubmit } from 'hooks/useSubmit';
import { getBody } from './StaticWeightedHelpers';
import {
  StaticWeighted,
  StaticWeightedLineItem,
  StaticWeightedTimeWindow as StaticWeightedTimeWindowType,
} from 'types/StaticWeighted';
import { StaticWeightedTimeWindow } from './StaticWeightedTimeWindow';
import { showSuccess } from 'helpers/showSuccess';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata';
import { SmallDisplay } from './components/SmallDisplay';
import { DefaultDisplay } from './components/DefaultDisplay';
import { StaticWeightedTimeWindowHeader } from './components/TimeWindowHeader';
import { useTimeWindow } from './hooks/useTimeWindow';
import {
  multiplyWithHighPrecisionNumbers,
  roundWithHighPrecision,
} from 'helpers/formatters/number';
import { TimeWindowsEditTitle, TimeWindows } from 'components/TimeWindow';
import { BenchmarkForm } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import { BenchmarkTypes } from 'types/Benchmark';
import { getToDates } from 'helpers/timeWindow/getToDates';
import {
  staticWeightedTimeWindowShape,
  staticWeightedValidationSchema,
} from './StaticWeightedValidationScheme';
import { monthsOptions } from 'helpers/monthsOptions';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

type Props = {};

export const BenchmarkStaticWeighted: React.FC<Props> = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const resolver = useYupValidationResolver(staticWeightedValidationSchema());

  const methods = useHookForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
      lookThroughDepth: 1,
      pushTimeWindowsToPortfolioAnalytics: 'false',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    setError,
  } = methods;

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<StaticWeighted>();

  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);

  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const { timeWindows, timeWindowsFilled } =
    useTimeWindows<StaticWeightedTimeWindowType>({
      setValue,
      control,
      timeWindowSchema: staticWeightedTimeWindowShape,
    });
  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    getBody,
    originalTimeWindows: timeWindows,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    if (benchmark) {
      const isCustomMonth = benchmark.rebalanceFrequency?.customMonths;

      let values: any = {
        ...benchmark,
        rebalanceFrequencyCustomMonths: isCustomMonth
          ? monthsOptions.filter(
              (option: any) =>
                benchmark.rebalanceFrequency?.customMonths[option.value] ===
                true
            )
          : null,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map((item: any) => {
            const { lineItems, weightSum } = item.lineItems.reduce(
              (
                acc: { weightSum: number; lineItems: any[] },
                lineItem: StaticWeightedLineItem
              ) => {
                const weight = multiplyWithHighPrecisionNumbers(
                  lineItem.weight,
                  100
                );

                acc.lineItems.push({
                  returnComponent:
                    lineItem.returnComponentBenchmarkId ||
                    lineItem.returnComponentResourceId,
                  weight,
                  isLagged: lineItem.isLagged,
                  customLagDate: lineItem.customLagDate,
                  defaultLagDate: benchmark.defaultLagDate,
                  name: lineItem.returnComponentName,
                  returnComponentName: lineItem.returnComponentName,
                });

                acc.weightSum += weight;

                return acc;
              },
              { weightSum: 0, lineItems: [] }
            );

            const roundedWeightValue = roundWithHighPrecision(weightSum);
            return {
              toggleId: uuidV4(),
              fromDate: new Date(item.fromDate),
              lineItems,
              weightSum: roundedWeightValue,
            };
          })
        ),
      };

      reset(values);
    }
  }, [benchmark, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    handleSubmit,
    isLoading: isSubmitting || isLoadingBenchmark,
    onSubmit,
    useTimeWindow,
    benchmarkName: benchmark?.name,
    benchmarkId: benchmark?.id,
  };

  const renderContent = () => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.StaticWeighted}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          HeaderComponent={StaticWeightedTimeWindowHeader}
          TimeWindowComponent={StaticWeightedTimeWindow}
          defaultValues={{
            fromDate: new Date(),
            toggleId: uuidV4(),
            assetSelectionSetId: '',
            tagRefPropertyDefinitionId: '',
            tagIds: '',
            lineItems: [],
            weightSum: 0,
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} />;
  };

  return (
    <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
      <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
      {benchmark?.id && !isSubmitting ? (
        <BenchmarkMetadata
          isTimeWindowsScreen={isTimeWindowsScreen}
          benchmark={benchmark}
          onConfirm={refetch}
        >
          <SmallDisplay benchmark={benchmark} />
          <DefaultDisplay benchmark={benchmark} />
        </BenchmarkMetadata>
      ) : null}
      <FormProvider {...methods} control={control}>
        {benchmark === undefined ? (
          <div style={{ padding: 20 }}>
            <BenchmarkFormTitle>
              Add New Static Weighted Benchmark
            </BenchmarkFormTitle>
            <BenchmarkForm
              benchmark={benchmark}
              benchmarkType={BenchmarkTypes.StaticWeighted}
              onLoadComplete={() => setIsFormLoadComplete(true)}
            />
          </div>
        ) : null}
        {renderContent()}
        <Footer
          showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
          onSave={handleSubmit((data: any) => {
            if (!data.pushTimeWindowsToPortfolioAnalytics) {
              delete data['lookThroughDepth'];
            }

            onSubmit({
              ...data,
              onSaveSuccess: isSuccess,
            });
          })}
          onDiscardChanges={() => {
            resetData();
            handleTWNavigation();
            window.scrollTo(0, 0);
          }}
          isSaveDisabled={!isDirty || errors.timeWindows}
          isLoading={tableTabbedProps.isLoading}
          showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
          discardText='Cancel'
          isDirty={isDirty}
        />
        <DevTool control={control} placement='top-left' />
      </FormProvider>
    </LoadingWrapper>
  );
};
